import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import Favicon from '../../../atoms/Favicon';
import PlatformIcon from '../../../atoms/PlatformIcon';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';
import { formatDate } from '../../../../utils/contentProcessor';
import ArticleModalActionMenu from './ArticleModalActionMenu';

const ArticleModalHeaderSubtitle = ({ article }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
    {(article.siteName || article.mediaType) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        <Favicon
          url={article.favicon || article.faviicon || article.siteFavicon || article.site_favicon || article.url || article.siteUrl}
          size={16}
          mediaType={article.mediaType}
        />
        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
          {article.siteName || article.mediaType}
          {article.section ? ` • ${article.section}` : ''}
        </Typography>
      </Box>
    )}

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
      <ScheduleIcon sx={{ fontSize: 16 }} />
      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
        {formatDate(article.date || article.publishedAt || article.createdAt)}
      </Typography>
    </Box>

    {article.author && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
        <PersonIcon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
          {article.author}
        </Typography>
      </Box>
    )}
  </Box>
);

const ArticleModalHeader = ({
  actionAnchorEl,
  article,
  isMobile,
  kpiaAnalyzing,
  loading,
  onAddToCollection,
  onAnalyzeKpia,
  onBack,
  onClose,
  onCloseActionMenu,
  onDelete,
  onEdit,
  onOpenExposureMenu,
  onOpenPublicLink,
  onOpenSentimentMenu,
  onOpenShare,
  onOpenTagDialog,
  onToggleFavorite,
  onToggleRead,
  qualitativeAnalysisApplied,
  readOnly,
  showKpia
}) => (
  <Box sx={{ mb: 2, mx: { xs: 0, md: -3 }, mt: { xs: 0, md: -3 } }}>
    <PlatformDialogTitle
      hideCloseButton={!readOnly}
      title={loading ? 'Carregando Artigo...' : article.title}
      subtitleContent={loading ? null : <ArticleModalHeaderSubtitle article={article} />}
      stacked
      eyebrow={article.mediaType}
      icon={<PlatformIcon mediaType={article.mediaType} />}
      onClose={onClose}
      onBack={isMobile ? undefined : onBack}
      titleLink={!loading ? (article.url || article.siteUrl) : null}
    >
      {!loading && (
        <ArticleModalActionMenu
          article={article}
          anchorEl={actionAnchorEl}
          isMobile={isMobile}
          onClose={onCloseActionMenu}
          onOpenShare={onOpenShare}
          onOpenPublicLink={onOpenPublicLink}
          onToggleFavorite={onToggleFavorite}
          onToggleRead={onToggleRead}
          onOpenTagDialog={onOpenTagDialog}
          onAddToCollection={onAddToCollection}
          onOpenSentimentMenu={onOpenSentimentMenu}
          onOpenExposureMenu={onOpenExposureMenu}
          onEdit={onEdit}
          onDelete={onDelete}
          showKpia={showKpia}
          kpiaAnalyzing={kpiaAnalyzing}
          qualitativeAnalysisApplied={qualitativeAnalysisApplied}
          onAnalyzeKpia={onAnalyzeKpia}
          readOnly={readOnly}
        />
      )}
    </PlatformDialogTitle>
  </Box>
);

const articleShape = {
  _id: PropTypes.string,
  author: PropTypes.string,
  createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  favicon: PropTypes.string,
  faviicon: PropTypes.string,
  mediaType: PropTypes.string,
  publishedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  section: PropTypes.string,
  site_favicon: PropTypes.string,
  siteFavicon: PropTypes.string,
  siteName: PropTypes.string,
  siteUrl: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string
};

ArticleModalHeaderSubtitle.propTypes = {
  article: PropTypes.shape(articleShape).isRequired
};

ArticleModalHeader.propTypes = {
  actionAnchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  article: PropTypes.shape(articleShape).isRequired,
  isMobile: PropTypes.bool,
  kpiaAnalyzing: PropTypes.bool,
  loading: PropTypes.bool,
  onAddToCollection: PropTypes.func,
  onAnalyzeKpia: PropTypes.func,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseActionMenu: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onOpenExposureMenu: PropTypes.func,
  onOpenPublicLink: PropTypes.func,
  onOpenSentimentMenu: PropTypes.func,
  onOpenShare: PropTypes.func,
  onOpenTagDialog: PropTypes.func,
  onToggleFavorite: PropTypes.func,
  onToggleRead: PropTypes.func,
  qualitativeAnalysisApplied: PropTypes.bool,
  readOnly: PropTypes.bool,
  showKpia: PropTypes.bool
};

ArticleModalHeader.defaultProps = {
  actionAnchorEl: null,
  isMobile: false,
  kpiaAnalyzing: false,
  loading: false,
  onAddToCollection: () => {},
  onAnalyzeKpia: () => {},
  onCloseActionMenu: () => {},
  onDelete: null,
  onEdit: null,
  onOpenExposureMenu: () => {},
  onOpenPublicLink: () => {},
  onOpenSentimentMenu: () => {},
  onOpenShare: () => {},
  onOpenTagDialog: () => {},
  onToggleFavorite: () => {},
  onToggleRead: () => {},
  qualitativeAnalysisApplied: false,
  readOnly: false,
  showKpia: false
};

export default ArticleModalHeader;
